import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useCompany } from '../contexts/CompanyContext';
import DashboardMetrics from './DashboardMetrics';
import MetricListView from './MetricListView';
import DetailView from './DetailView';
import NewCallWizard from './NewCallWizard';
import NewRecurringCallWizard from './NewRecurringCallWizard';
import AdminManagement from './AdminManagement';
import Reports from './Reports';
import RideHistory from './RideHistory';
import AccountsManager from './AccountsManager';
import DriverTracking from './DriverTracking';
import BroadcastMessagingModal from './BroadcastMessagingModal';
import DriverMessagingModal from './DriverMessagingModal';
import NotificationPanel from './NotificationPanel';
import ChangePasswordDialog from './ChangePasswordDialog';
import { adminAPI } from '../services/adminService';
import { messagesAPI } from '../services/apiService';
import soundService from '../services/soundService';
import { safeInvoke } from '../utils/signalRHelper';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Badge,
  Tabs,
  Tab,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Campaign as CampaignIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Lock as LockIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AddCall as AddCallIcon,
  Repeat as RepeatIcon,
  LocationOn as TrackingIcon,
  History as HistoryIcon,
  Assessment as ReportsIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material';
import { useAlert } from '../contexts/AlertContext';

const MainDashboard = () => {
  const { user, logout, signalRConnection, connectSignalR } = useAuth();
  const { settings } = useSettings();
  const { company, companyCode } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();

  // Hide header on scroll down, show on scroll up
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY <= 10) {
        setHeaderVisible(true);
      } else if (currentScrollY > lastScrollY.current + 5) {
        setHeaderVisible(false);
      } else if (currentScrollY < lastScrollY.current - 5) {
        setHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Base path for navigation (company-scoped)
  const basePath = companyCode ? `/${companyCode}/dispatch` : '/dispatch';
  const nav = (sub = '') => navigate(sub ? `${basePath}/${sub}` : basePath);

  // Navigation state
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'list', 'detail', 'newCall', 'recurringCall', 'admin', 'reports', 'history'
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [selectedMetricData, setSelectedMetricData] = useState([]);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const { showToast } = useAlert();

  // Admin access state
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);
  // Settings dirty state — lifted so top-level tabs can guard navigation
  const [settingsDirty, setSettingsDirty] = useState(false);

  // Broadcast modal state
  const [showBroadcast, setShowBroadcast] = useState(false);

  // Change password dialog state
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Incoming message state - tracks unread messages from drivers
  const [unreadDriverMessages, setUnreadDriverMessages] = useState([]); // Array of { driverId, driverName, messageId, message }
  const [driversWithUnread, setDriversWithUnread] = useState(new Map()); // Map of driver IDs to unread count
  const [openMessagingDriverId, setOpenMessagingDriverId] = useState(null); // Track which driver's messaging modal is open

  // Snackbar for incoming message alerts
  const [snackbar, setSnackbar] = useState({ open: false, message: '', driverId: null, driverName: '' });

  // Sound notification state
  const [soundEnabled, setSoundEnabled] = useState(soundService.isEnabled());

  // RideHistory search state - for navigating from clickable messages
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // Track where detail view was navigated from (for proper back navigation)
  const [detailFromContext, setDetailFromContext] = useState(null); // { from: 'history', searchQuery: '' } or null

  // Mobile nav drawer state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Track SignalR connection status for UI feedback
  const [signalRReady, setSignalRReady] = useState(false);

  // Track unreviewed ride IDs for review mode navigation
  const [unreviewedRideIds, setUnreviewedRideIds] = useState([]);

  // Connect to SignalR when dashboard loads (lazy connection)
  useEffect(() => {
    const initConnection = async () => {
      if (connectSignalR) {
        await connectSignalR();
        setSignalRReady(true);
      }
    };
    initConnection();
  }, [connectSignalR]);

  // Update signalRReady when connection state changes
  useEffect(() => {
    if (signalRConnection?.state === 'Connected') {
      setSignalRReady(true);
    } else {
      setSignalRReady(false);
    }
  }, [signalRConnection?.state]);

  // Sync state with URL on mount and location change
  useEffect(() => {
    const path = location.pathname;
    const search = new URLSearchParams(location.search);

    // Always check for messaging modal via query param (works on any view)
    const messagingDriverId = search.get('messagingDriverId');
    if (messagingDriverId) {
      setOpenMessagingDriverId(parseInt(messagingDriverId));
    } else {
      setOpenMessagingDriverId(null);
    }

    // Handle view routing
    if (path.includes('/new-call')) {
      setCurrentView('newCall');
    } else if (path.includes('/recurring-call')) {
      setCurrentView('recurringCall');
    } else if (path.includes('/admin')) {
      setCurrentView('admin');
    } else if (path.includes('/reports')) {
      setCurrentView('reports');
    } else if (path.includes('/tracking')) {
      setCurrentView('tracking');
    } else if (path.includes('/accounts')) {
      setCurrentView('accounts');
    } else if (path.includes('/history')) {
      setCurrentView('history');
    } else if (path.includes('/detail')) {
      setCurrentView('detail');
      setSelectedItemType(search.get('type'));
      setSelectedItemId(search.get('id'));
    } else if (path.includes('/list')) {
      setCurrentView('list');
      setSelectedMetric(search.get('metric'));
    } else {
      setCurrentView('dashboard');
    }
  }, [location]);

  useEffect(() => {
    checkAdminAccess();
    loadUnreadMessages();

    // Set up SignalR listeners for incoming driver messages
    if (signalRConnection) {
      // Listen for messages from drivers to dispatchers
      const handleDriverMessage = async (messageData) => {
        // Only process messages FROM drivers (not broadcasts or dispatcher messages)
        // Check both lowercase and PascalCase since C# serializes with PascalCase
        const from = (messageData.from || messageData.From || '').toLowerCase();
        const isFromDriver = from.startsWith('driver');

        if (!isFromDriver) {
          return;
        }

        const driverId = messageData.driverId || messageData.DriverId;
        const driverName = messageData.driverName || messageData.DriverName || `Driver #${driverId}`;
        const message = messageData.message || messageData.Message;
        const messageId = messageData.id || messageData.Id;


        // If NotificationPanel OR the messaging modal for this driver is open, mark as read immediately
        const isModalOpenForDriver = openMessagingDriverId === driverId;
        if ((showNotifications || isModalOpenForDriver) && messageId) {
          try {
            await safeInvoke({
              connection: signalRConnection,
              connectFn: connectSignalR,
              methodName: 'MarkMessagesAsRead',
              args: [[messageId], 'dispatcher']
            });
            // Don't add to unread or increment count - panel/modal is open
            // Still play sound and show snackbar
            if (message) {
              soundService.playMessageSound(message);
            }
            const truncatedMessage = message && message.length > 50
              ? message.substring(0, 50) + '...'
              : (message || 'New message');
            setSnackbar({
              open: true,
              message: truncatedMessage,
              driverId,
              driverName
            });
            return; // Don't add to unread messages
          } catch (error) {
            console.error('Error marking incoming message as read via SignalR:', error);
            // Fall through to add as unread if marking fails
          }
        }

        // NotificationPanel is closed - add to unread messages
        // Play sound notification for incoming message
        if (message) {
          soundService.playMessageSound(message);
        } else {
          console.warn('No message text found in messageData:', messageData);
        }

        // Add to unread messages
        setUnreadDriverMessages(prev => {
          const updated = [...prev, {
            driverId,
            driverName,
            messageId,
            message,
            timestamp: new Date().toISOString()
          }];
          return updated;
        });

        // Add driver to map with incremented count
        setDriversWithUnread(prev => {
          const newMap = new Map(prev);
          const currentCount = newMap.get(driverId) || 0;
          newMap.set(driverId, currentCount + 1);
          return newMap;
        });

        // Update notification count
        setNotificationCount(prev => prev + 1);

        // Show snackbar alert
        const truncatedMessage = message && message.length > 50
          ? message.substring(0, 50) + '...'
          : (message || 'New message');
        setSnackbar({
          open: true,
          message: truncatedMessage,
          driverId,
          driverName
        });
      };

      signalRConnection.on('ReceiveMessage', handleDriverMessage);

      // Listen for read receipts - when driver marks our message as read
      const handleMessageMarkedAsRead = (data) => {

        // data = { messageId, driverId, markedBy: 'driver', timestamp }
        const { messageId, driverId, markedBy } = data;

        // IMPORTANT: Only process if the DRIVER marked it as read
        // Don't process if we (dispatcher) marked it as read - we already handled it optimistically
        if (markedBy === 'dispatcher') {
          return;
        }

        if (driverId && messageId) {
          // Remove this specific message from unread list
          setUnreadDriverMessages(prev => {
            const updated = prev.filter(msg => msg.messageId !== messageId && msg.id !== messageId);

            // Update the badge count for this driver
            const remainingForDriver = updated.filter(msg => msg.driverId === driverId);
            setDriversWithUnread(prevMap => {
              const newMap = new Map(prevMap);
              if (remainingForDriver.length === 0) {
                // No more unread messages from this driver
                newMap.delete(driverId);
              } else {
                // Update count
                newMap.set(driverId, remainingForDriver.length);
              }
              return newMap;
            });

            // Update total notification count
            setNotificationCount(updated.length);

            return updated;
          });

        }
      };

      signalRConnection.on('MessageMarkedAsRead', handleMessageMarkedAsRead);

      return () => {
        signalRConnection.off('ReceiveMessage', handleDriverMessage);
        signalRConnection.off('MessageMarkedAsRead', handleMessageMarkedAsRead);
      };
    }

    // SignalR pushes new messages in real-time; polling is a fallback safety net
    const interval = setInterval(loadUnreadMessages, 60000); // Check every 60 seconds
    return () => {
      clearInterval(interval);
    };
  }, [signalRConnection, showNotifications, openMessagingDriverId]);

  const checkAdminAccess = async () => {
    try {
      setRoleLoading(true);

      if (!user?.userId) {
        setIsAdmin(false);
        return;
      }

      const roleData = await adminAPI.user.isAdmin(user.userId);

      // The API returns { isAdmin: boolean }
      setIsAdmin(roleData.isAdmin);
    } catch (error) {
      console.error('Failed to check admin access:', error);
      setIsAdmin(false);
    } finally {
      setRoleLoading(false);
    }
  };

  const loadUnreadMessages = async () => {
    try {
      // Fetch unread messages from drivers
      const unreadMessages = await messagesAPI.getUnread();
      const messagesArray = unreadMessages || [];

      // Update unread driver messages
      setUnreadDriverMessages(messagesArray);

      // Build map of driver IDs to unread message counts
      const unreadCountMap = new Map();
      messagesArray.forEach(msg => {
        const count = unreadCountMap.get(msg.driverId) || 0;
        unreadCountMap.set(msg.driverId, count + 1);
      });
      setDriversWithUnread(unreadCountMap);

      // Update notification count
      setNotificationCount(messagesArray.length);
    } catch (error) {
      console.error('Failed to load unread messages:', error);
    }
  };

  /**
   * Mark messages from a specific driver as read
   * Called when opening the messaging modal for that driver
   */
  const markDriverMessagesAsRead = useCallback(async (driverId) => {
    try {

      // Track that this driver's modal is open (suppresses badge)
      setOpenMessagingDriverId(driverId);

      // Get message IDs for this driver
      const messageIds = unreadDriverMessages
        .filter(msg => msg.driverId === driverId)
        .map(msg => msg.messageId || msg.id)
        .filter(id => id); // Filter out undefined/null


      // Update local state FIRST (optimistic update)
      setUnreadDriverMessages(prev => {
        const filtered = prev.filter(msg => msg.driverId !== driverId);
        return filtered;
      });

      setDriversWithUnread(prev => {
        const newMap = new Map(prev);
        newMap.delete(driverId);
        return newMap;
      });

      setNotificationCount(prev => {
        const newCount = Math.max(0, prev - messageIds.length);
        return newCount;
      });

      // Then mark as read on server via SignalR
      if (messageIds.length > 0 && signalRConnection) {
        await safeInvoke({
          connection: signalRConnection,
          connectFn: connectSignalR,
          methodName: 'MarkMessagesAsRead',
          args: [messageIds, 'dispatcher']
        });
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [unreadDriverMessages, signalRConnection]);

  /**
   * Called when messaging modal closes
   */
  const handleMessagingModalClose = useCallback(() => {
    setOpenMessagingDriverId(null);

    // Remove messagingDriverId from URL while preserving other params
    const search = new URLSearchParams(location.search);
    search.delete('messagingDriverId');
    const newSearch = search.toString();
    navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
  }, [location, navigate]);

  /**
   * Open messaging modal with URL update
   */
  const openMessagingModal = useCallback((driverId) => {
    // Set state directly for immediate effect
    setOpenMessagingDriverId(driverId);

    // Also add messagingDriverId to URL while preserving other params (for refresh/reload)
    const search = new URLSearchParams(location.search);
    search.set('messagingDriverId', driverId);
    navigate(`${location.pathname}?${search.toString()}`, { replace: false });
  }, [location, navigate, currentView]);

  /**
   * Get badge counts with the currently open modal's driver suppressed
   */
  const getFilteredBadgeCounts = useCallback(() => {
    if (!openMessagingDriverId) {
      return driversWithUnread;
    }

    // Return a new Map without the currently open driver
    const filtered = new Map(driversWithUnread);
    filtered.delete(openMessagingDriverId);
    return filtered;
  }, [driversWithUnread, openMessagingDriverId]);

  /**
   * Navigate to RideHistory with a specific search query
   * Used when clicking on a "Cancel Call" or "Reassign Call" message
   */
  const navigateToRideHistory = useCallback((rideId) => {

    setHistorySearchQuery(rideId.toString());
    setCurrentView('history');
    // Close messaging modal
    setOpenMessagingDriverId(null);

    // First navigate to history
    nav('history');

    // Then clean up the URL after a short delay to ensure navigation completes
    setTimeout(() => {
      const search = new URLSearchParams(window.location.search);
      search.delete('messagingDriverId');
      const searchString = search.toString();
      const cleanUrl = `${basePath}/history${searchString ? '?' + searchString : ''}`;
      navigate(cleanUrl, { replace: true });
    }, 50);
  }, [navigate]);



  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMetricClick = (metricType, data) => {
    setSelectedMetric(metricType);
    setSelectedMetricData(data);
    setCurrentView('list');
    navigate(`${basePath}/list?metric=${metricType}`);

    // Track unreviewed ride IDs for review mode
    if (metricType === 'completedUnreviewedRides' && Array.isArray(data)) {
      setUnreviewedRideIds(data.map(ride => ride.rideId));
    } else {
      setUnreviewedRideIds([]);
    }
  };

  const handleItemClick = (itemType, itemId, context = null) => {
    setSelectedItemType(itemType);
    setSelectedItemId(itemId);
    setDetailFromContext(context); // Store where we came from
    setCurrentView('detail');
    navigate(`${basePath}/detail?type=${itemType}&id=${itemId}`);
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedMetric(null);
    setSelectedMetricData([]);
    setSelectedItemType(null);
    setSelectedItemId(null);
    nav();
  };

  const handleBackToList = () => {
    // Check if we came from history
    if (detailFromContext?.from === 'history') {
      setCurrentView('history');
      setSelectedItemType(null);
      setSelectedItemId(null);
      // Navigate back to history with the search query preserved
      const searchParam = detailFromContext.searchQuery ? `?search=${detailFromContext.searchQuery}` : '';
      navigate(`${basePath}/history${searchParam}`);
      setDetailFromContext(null);
    } else {
      setCurrentView('list');
      setSelectedItemType(null);
      setSelectedItemId(null);
      navigate(`${basePath}/list?metric=${selectedMetric}`);
    }
  };

  // Handler for review mode: mark current ride as reviewed and navigate to next
  const handleReviewAndNext = (reviewedRideId) => {
    // Remove the reviewed ride from the list
    const updatedIds = unreviewedRideIds.filter(id => id !== reviewedRideId);
    setUnreviewedRideIds(updatedIds);

    // Also update the selectedMetricData to keep list in sync
    setSelectedMetricData(prev => prev.filter(ride => ride.rideId !== reviewedRideId));

    if (updatedIds.length > 0) {
      // Navigate to the next ride
      const nextRideId = updatedIds[0];
      setSelectedItemId(nextRideId);
      navigate(`${basePath}/detail?type=ride&id=${nextRideId}`);
    } else {
      // All rides reviewed - go back to dashboard
      showToast('All rides reviewed!', 'success');
      handleBackToDashboard();
    }
  };

  // const handleShowNewCall = () => {
  //   setCurrentView('newCall');
  // };

  const handleNewCallComplete = () => {
    loadUnreadMessages();
  };

  const handleNewCallCancel = () => {
    setCurrentView('dashboard');
    nav();
  };

  const handleRecurringCallComplete = () => {
    loadUnreadMessages();
  };

  const handleRecurringCallCancel = () => {
    setCurrentView('dashboard');
    nav();
  };

  const handleShowNotifications = () => {
    setShowNotifications(true);
  };

  const handleCloseNotifications = () => {
    setShowNotifications(false);
    // Don't refresh - SignalR events keep the state updated in real-time
  };

  const handleShowBroadcast = () => {
    setShowBroadcast(true);
  };

  const handleCloseBroadcast = () => {
    setShowBroadcast(false);
  };

  const handleShowChangePassword = () => {
    setShowChangePassword(true);
  };

  const handleCloseChangePassword = () => {
    setShowChangePassword(false);
  };

  const handlePasswordChangeSuccess = () => {
    showToast('Password updated successfully!', 'success');

  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleToggleSound = () => {
    const newState = soundService.toggle();
    setSoundEnabled(newState);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'list':
        return (
          <MetricListView
            isAdmin={isAdmin}
            metricType={selectedMetric}
            data={selectedMetricData}
            onItemClick={handleItemClick}
            onBackToDashboard={handleBackToDashboard}
            driversWithUnread={getFilteredBadgeCounts()}
            onMarkMessagesRead={markDriverMessagesAsRead}
            onMessagingModalClose={handleMessagingModalClose}
            onNavigateToRideHistory={navigateToRideHistory}
            openMessagingModal={openMessagingModal}
            openMessagingDriverId={openMessagingDriverId}
          />
        );

      case 'detail':
        return (
          <DetailView
            isAdmin={isAdmin}
            itemType={selectedItemType}
            itemId={selectedItemId}
            onBackToList={handleBackToList}
            metricType={selectedMetric}
            unreviewedRideIds={unreviewedRideIds}
            onReviewAndNext={handleReviewAndNext}
            onBackToDashboard={handleBackToDashboard}
          />
        );

      case 'newCall':
        return (
          <NewCallWizard
            onComplete={handleNewCallComplete}
            onCancel={handleNewCallCancel}
          />
        );

      case 'recurringCall':
        return (
          <NewRecurringCallWizard
            onComplete={handleRecurringCallComplete}
            onCancel={handleRecurringCallCancel}
          />
        );

      case 'admin':
        return <AdminManagement onNavigateToRideHistory={navigateToRideHistory} openMessagingModal={openMessagingModal} openMessagingDriverId={openMessagingDriverId} onMessagingModalClose={handleMessagingModalClose} onMarkMessagesRead={markDriverMessagesAsRead} onDirtyChange={setSettingsDirty} />;

      case 'reports':
        return <Reports />;

      case 'history':
        return (
          <RideHistory
            isAdmin={isAdmin}
            onItemClick={handleItemClick}
            initialSearchQuery={historySearchQuery}
            onSearchQueryUsed={() => setHistorySearchQuery('')}
          />
        );

      case 'accounts':
        return <AccountsManager />;

      case 'tracking':
        return <DriverTracking isAdmin={isAdmin} openMessagingModal={openMessagingModal} />;

      default: // 'dashboard'
        return (
          <div className="dashboard-main">
            <DashboardMetrics
              onMetricClick={handleMetricClick}
              hasUnreadMessages={driversWithUnread.size > 0}
            />
          </div>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f5f7fa', minHeight: '100svh', overflowX: 'hidden' }}>
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: '#1a1a2e',
          color: 'white',
          boxShadow: 1,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ gap: 1 }}>

          {/* ── MOBILE: hamburger only ── */}
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{ color: 'white', display: { xs: 'flex', sm: 'none' } }}
          >
            <Badge badgeContent={notificationCount} color="error">
              <MenuIcon />
            </Badge>
          </IconButton>

          <Typography variant="h6" component="div" sx={{ color: 'white', whiteSpace: 'nowrap', flexShrink: 0, display: { xs: 'none', sm: 'block' }, mr: { sm: 2, lg: 4 } }}>
            {company?.name || 'Dispatcher Dashboard'}
          </Typography>

          {/* ── DESKTOP: inline tabs ── */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', overflow: 'hidden', flex: 1, minWidth: 0 }}>
            <Tabs
              value={['list', 'detail'].includes(currentView) ? 'dashboard' : currentView}
              onChange={(e, newValue) => {
                if (settingsDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
                setSettingsDirty(false);
                setCurrentView(newValue);
                if (newValue === 'dashboard') nav();
                else if (newValue === 'newCall') nav('new-call');
                else if (newValue === 'recurringCall') nav('recurring-call');
                else if (newValue === 'admin') nav('admin');
                else if (newValue === 'reports') nav('reports');
                else if (newValue === 'tracking') nav('tracking');
                else if (newValue === 'history') nav('history');
                else if (newValue === 'accounts') nav('accounts');
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 'auto',
                flex: 1,
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.7)', '&.Mui-selected': { color: 'white' } },
                '& .MuiTabs-indicator': { backgroundColor: 'white' },
                '& .MuiTabs-scrollButtons': { color: 'rgba(255,255,255,0.7)' },
              }}
            >
              <Tab label="Dashboard" value="dashboard" />
              <Tab label="New Call" value="newCall" />
              {settings.showRecurringCallsTab && <Tab label="Recurring Call" value="recurringCall" />}
              <Tab label="Tracking" value="tracking" />
              <Tab label="History" value="history" />
              <Tab label="Accounts" value="accounts" />
              {!roleLoading && isAdmin && settings.showReportsTab && <Tab label="Reports" value="reports" />}
              {!roleLoading && isAdmin && <Tab label="Admin" value="admin" />}
            </Tabs>
          </Box>

          {/* ── DESKTOP: action buttons ── */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <IconButton onClick={handleShowBroadcast} sx={{ color: 'rgba(255,255,255,0.9)' }} title="Broadcast to All Drivers">
              <CampaignIcon />
            </IconButton>
            <Tooltip title={soundEnabled ? 'Sound on' : 'Sound off'}>
              <IconButton onClick={handleToggleSound} sx={{ color: soundEnabled ? '#4fc3f7' : 'rgba(255,255,255,0.5)' }}>
                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleShowNotifications} sx={{ color: 'rgba(255,255,255,0.9)' }}>
              <Badge badgeContent={openMessagingDriverId ? unreadDriverMessages.filter(m => m.driverId !== openMessagingDriverId).length : notificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Typography variant="body2" sx={{ mx: 1, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', display: { sm: 'none', lg: 'block' } }}>
              Welcome, {user?.username}
            </Typography>
            <Tooltip title="Change Password">
              <IconButton onClick={handleShowChangePassword} sx={{ color: 'rgba(255,255,255,0.9)' }}>
                <LockIcon />
              </IconButton>
            </Tooltip>
            <Button
              onClick={handleLogout}
              sx={{ color: '#ff6b6b', borderColor: '#ff6b6b', '&:hover': { borderColor: '#ff5252', backgroundColor: 'rgba(255,107,107,0.1)' } }}
              startIcon={<LogoutIcon />}
              variant="outlined"
              size="small"
            >
              Logout
            </Button>
          </Box>

          {/* Mobile: spacer + title centered */}
          <Typography variant="subtitle1" component="div" sx={{ color: 'white', flex: 1, textAlign: 'center', display: { xs: 'block', sm: 'none' } }}>
            Dispatcher
          </Typography>

        </Toolbar>
      </AppBar>

      {/* Spacer to compensate for fixed AppBar */}
      <Box sx={{ height: { xs: '56px', sm: '64px' }, flexShrink: 0 }} />

      {/* ── MOBILE DRAWER MENU ── */}
      <Drawer
        anchor="top"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{ display: { sm: 'none' }, '& .MuiDrawer-paper': { bgcolor: '#1a1a2e', color: 'white', pt: 1 } }}
      >
        {/* Nav items */}
        <List dense>
          {[
            { label: 'Dashboard', value: 'dashboard', icon: <DashboardIcon /> },
            { label: 'New Call', value: 'newCall', icon: <AddCallIcon /> },
            ...(settings.showRecurringCallsTab ? [{ label: 'Recurring Call', value: 'recurringCall', icon: <RepeatIcon /> }] : []),
            { label: 'Tracking', value: 'tracking', icon: <TrackingIcon /> },
            { label: 'History', value: 'history', icon: <HistoryIcon /> },
            { label: 'Accounts', value: 'accounts', icon: <AccountIcon /> },
            ...(!roleLoading && isAdmin && settings.showReportsTab ? [{ label: 'Reports', value: 'reports', icon: <ReportsIcon /> }] : []),
            ...(!roleLoading && isAdmin ? [{ label: 'Admin', value: 'admin', icon: <AdminIcon /> }] : []),
          ].map(item => (
            <ListItem key={item.value} disablePadding>
              <ListItemButton
                selected={(['list', 'detail'].includes(currentView) ? 'dashboard' : currentView) === item.value}
                onClick={() => {
                  if (settingsDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
                  setSettingsDirty(false);
                  setMobileMenuOpen(false);
                  setCurrentView(item.value);
                  if (item.value === 'dashboard') nav();
                  else if (item.value === 'newCall') nav('new-call');
                  else if (item.value === 'recurringCall') nav('recurring-call');
                  else if (item.value === 'admin') nav('admin');
                  else if (item.value === 'reports') nav('reports');
                  else if (item.value === 'tracking') nav('tracking');
                  else if (item.value === 'history') nav('history');
                  else if (item.value === 'accounts') nav('accounts');
                }}
                sx={{ color: 'white', '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.15)', color: 'white' } }}
              >
                <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.5 }} />

        {/* Action items */}
        <List dense>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setMobileMenuOpen(false); handleShowBroadcast(); }} sx={{ color: 'white' }}>
              <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}><CampaignIcon /></ListItemIcon>
              <ListItemText primary="Broadcast to Drivers" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setMobileMenuOpen(false); handleToggleSound(); }} sx={{ color: 'white' }}>
              <ListItemIcon sx={{ color: soundEnabled ? '#4fc3f7' : 'rgba(255,255,255,0.5)', minWidth: 40 }}>
                {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </ListItemIcon>
              <ListItemText primary={soundEnabled ? 'Sound: On' : 'Sound: Off'} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setMobileMenuOpen(false); handleShowNotifications(); }} sx={{ color: 'white' }}>
              <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                <Badge badgeContent={notificationCount} color="error"><NotificationsIcon /></Badge>
              </ListItemIcon>
              <ListItemText primary={`Messages${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setMobileMenuOpen(false); handleShowChangePassword(); }} sx={{ color: 'white' }}>
              <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}><LockIcon /></ListItemIcon>
              <ListItemText primary="Change Password" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setMobileMenuOpen(false); handleLogout(); }} sx={{ color: '#ff6b6b' }}>
              <ListItemIcon sx={{ color: '#ff6b6b', minWidth: 40 }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Content */}
      {currentView === 'admin' ? (
        <Box sx={{ height: { xs: 'calc(100svh - 56px)', sm: 'calc(100svh - 64px)' }, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          {renderContent()}
        </Box>
      ) : (
        <Container maxWidth="xl" sx={{ py: 3, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {renderContent()}
        </Container>
      )}

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={handleCloseNotifications}
        unreadMessages={openMessagingDriverId ? unreadDriverMessages.filter(msg => msg.driverId !== openMessagingDriverId) : unreadDriverMessages}
        onMessageClick={openMessagingModal}
        onMarkMessagesRead={markDriverMessagesAsRead}
        onNavigateToRideHistory={navigateToRideHistory}
      />

      {/* Broadcast Messaging Modal */}
      <BroadcastMessagingModal
        isOpen={showBroadcast}
        onClose={handleCloseBroadcast}
      />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showChangePassword}
        onClose={handleCloseChangePassword}
        userId={user?.userId}
        onSuccess={handlePasswordChangeSuccess}
      />

      {/* Global Driver Messaging Modal - for opening from notifications/dashboard */}
      {/* Only show when openMessagingDriverId is set AND we're NOT on list view (which has its own modal) */}
      {openMessagingDriverId && currentView !== 'list' && (
        <DriverMessagingModal
          isOpen={true}
          onClose={handleMessagingModalClose}
          driverId={openMessagingDriverId}
          onNavigateToRideHistory={navigateToRideHistory}
        />
      )}

      {/* Incoming Message Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            New message from {snackbar.driverName}
          </Typography>
          <Typography variant="body2">
            {snackbar.message}
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainDashboard;